<template>
  <!-- Ventana propia (no modal de bootstrap) para que funcione igual en la captura movil y en el modulo completo -->
  <div v-if="visible" class="tc-overlay" @click.self="cerrar">
    <div class="tc-panel">
      <div class="tc-header">
        <div>
          <b>Trasladar a tienda</b>
          <div class="small text-muted">
            <template v-if="destinoActual">
              Se creará una cotización nueva en la tienda {{ textoTiendaDestino }} con el cliente y los productos de
              la cotización elegida. La original quedará anulada.
            </template>
            <template v-else>
              Elija la tienda a donde se traslada y la cotización a trasladar. Se creará una cotización nueva en
              esa tienda y la original quedará anulada.
            </template>
          </div>
        </div>
        <button type="button" class="close ml-2" :disabled="trasladando" @click="cerrar">
          <span>&times;</span>
        </button>
      </div>

      <div class="tc-body">
        <!-- Desde el listado no hay cotizacion abierta: la tienda destino se elige aqui -->
        <div v-if="desdeListado" class="form-group mb-2">
          <label class="mb-1">Tienda a donde se traslada</label>
          <select v-model="destinoSeleccionado" class="form-control" :disabled="trasladando">
            <option value="">Seleccione la tienda destino</option>
            <option v-for="tienda in tiendasDestino" :key="'destino_' + tienda.codigo" :value="tienda.codigo">
              {{ tienda.codigo }} - {{ tienda.nombre }}
            </option>
          </select>
        </div>
        <div class="form-group mb-2">
          <label class="mb-1">Tienda donde se hizo la cotización</label>
          <select v-model="tiendaOrigen" class="form-control" :disabled="trasladando">
            <option value="">Seleccione la tienda</option>
            <option v-for="tienda in tiendasOrigen" :key="tienda.codigo" :value="tienda.codigo">
              {{ tienda.codigo }} - {{ tienda.nombre }}
            </option>
          </select>
        </div>
        <div class="form-group mb-2">
          <label class="mb-1">Número de cotización</label>
          <div class="input-group">
            <input v-model="numero" class="form-control" type="text" inputmode="numeric" placeholder="Número"
              autocomplete="off" :disabled="trasladando" @keyup.enter="buscar" />
            <div class="input-group-append">
              <button type="button" class="btn btn-dark" :disabled="buscando || trasladando" @click="buscar">
                <i class="fa fa-search"></i>
                Buscar
              </button>
            </div>
          </div>
        </div>

        <div v-if="error" class="alert alert-danger py-2 small">
          {{ error }}
          <ul v-if="erroresProductos.length > 0" class="tc-productos mb-0 mt-1">
            <li v-for="producto in erroresProductos" :key="'error_' + producto.sku">
              <b>{{ producto.sku }}</b> - {{ producto.nombre }}: {{ producto.motivo }}
            </li>
          </ul>
        </div>

        <div v-if="buscando" class="text-center text-muted py-2">Buscando...</div>
        <div v-else-if="buscado && resultados.length === 0" class="text-center text-muted py-2">
          No se encontró una cotización de requerimiento con ese número en la tienda seleccionada.
        </div>

        <!-- Coincidencias: solo se puede trasladar la que esta cerrada, vigente, sin requerimiento
             y con todos sus productos permitidos en la tienda destino -->
        <div v-for="cot in resultados" :key="cot.id" class="tc-item"
          :class="{ 'tc-item-bloqueada': !cot.puede_trasladar }">
          <div class="tc-fila">
            <span>
              <b>#{{ cot.numero }}</b>
              <small class="text-muted"> &middot; {{ cot.tienda }} - {{ cot.tienda_nombre }}</small>
            </span>
            <span>
              <span v-if="cot.esta_vencida" class="badge badge-danger mr-1">Vencida</span>
              <span class="badge" :class="{
                'badge-success': cot.estado == 'A',
                'badge-warning': cot.estado == 'B',
                'badge-danger': cot.estado == '9',
              }">{{ cot.estado_str }}</span>
            </span>
          </div>
          <div class="tc-cliente">({{ cot.cliente_nit }}) {{ cot.cliente_nombre }}</div>
          <small class="d-block text-muted">
            {{ formatoFecha(cot.fecha) }} &middot; Vence: {{ formatoFecha(cot.fecha_vence) }}
          </small>
          <div class="tc-fila mt-1">
            <small>{{ cot.productos }} {{ cot.productos === 1 ? "producto" : "productos" }}</small>
            <b class="text-success">Q {{ formatoMonto(cot.total) }}</b>
          </div>

          <div v-if="!cot.puede_trasladar" class="text-danger small mt-1">
            <i class="fas fa-exclamation-circle"></i>
            {{ cot.motivo }}
          </div>
          <ul v-if="cot.productos_no_permitidos && cot.productos_no_permitidos.length > 0"
            class="tc-productos small text-danger mb-0 mt-1">
            <li v-for="producto in cot.productos_no_permitidos" :key="cot.id + '_' + producto.sku">
              <b>{{ producto.sku }}</b> - {{ producto.nombre }}: {{ producto.motivo }}
            </li>
          </ul>

          <template v-if="cot.puede_trasladar">
            <div v-if="confirmando === cot.id" class="tc-confirmar">
              <div class="small mb-2">
                ¿Trasladar la cotización #{{ cot.numero }} de la tienda {{ cot.tienda }} a la tienda
                {{ destinoActual }}? La cotización #{{ cot.numero }} quedará anulada.
              </div>
              <div class="d-flex">
                <button type="button" class="btn btn-secondary flex-fill mr-2" :disabled="trasladando"
                  @click="confirmando = null">
                  Cancelar
                </button>
                <button type="button" class="btn btn-success flex-fill" :disabled="trasladando" @click="trasladar(cot)">
                  {{ trasladando ? "Trasladando..." : "Sí, trasladar" }}
                </button>
              </div>
            </div>
            <button v-else type="button" class="btn btn-success btn-block mt-2 tc-btn" @click="confirmando = cot.id">
              <i class="fa fa-exchange-alt"></i>
              Trasladar a la tienda {{ destinoActual }}
            </button>
          </template>
        </div>

        <button type="button" class="btn btn-secondary btn-block mt-3" :disabled="trasladando" @click="cerrar">
          Cerrar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import CotizacionMovilService from "@/services/cxc/CotizacionMovilService";

/**
 * Busca por tienda y numero una cotizacion de requerimiento de otra tienda y la
 * traslada a la tienda de la cotizacion actual (crea una nueva y anula la original).
 * Sin cotizacionId (desde el listado) la tienda destino se elige en la ventana,
 * entre las tiendas asignadas al usuario.
 * Todas las validaciones se repiten en el backend al trasladar.
 * Emite "trasladada" con { cotizacion_e, numero, advertencias }.
 */
export default {
  name: "TrasladarCotizacionModal",
  props: {
    // Cotizacion desde la que se ejecuta la accion: su tienda es la tienda destino
    cotizacionId: { type: String, default: null },
    tiendaDestino: { type: String, default: null },
  },
  data() {
    return {
      visible: false,
      destinoSeleccionado: "",
      tiendasDestino: [],
      tiendaOrigen: "",
      numero: "",
      resultados: [],
      buscado: false,
      buscando: false,
      trasladando: false,
      confirmando: null,
      error: "",
      erroresProductos: [],
    };
  },
  computed: {
    ...mapGetters({
      tiendas: "catalogos/tiendas",
    }),
    desdeListado() {
      return !this.cotizacionId;
    },
    // Tienda destino: la de la cotizacion abierta o la elegida en la ventana (listado)
    destinoActual() {
      return this.desdeListado ? this.destinoSeleccionado : this.tiendaDestino;
    },
    // La tienda destino no se ofrece como origen
    tiendasOrigen() {
      return (this.tiendas || []).filter((tienda) => tienda.codigo !== this.destinoActual);
    },
    textoTiendaDestino() {
      const destino = (this.tiendas || []).find((tienda) => tienda.codigo === this.destinoActual);
      return destino ? `${destino.codigo} - ${destino.nombre}` : this.destinoActual || "";
    },
  },
  watch: {
    // Las validaciones de productos dependen de la tienda destino: al cambiarla se repite la busqueda
    destinoSeleccionado(nuevo) {
      this.resultados = [];
      this.buscado = false;
      this.confirmando = null;
      this.limpiarError();
      if (nuevo && this.tiendaOrigen === nuevo) {
        this.tiendaOrigen = "";
      }
    },
  },
  methods: {
    abrir() {
      this.destinoSeleccionado = "";
      this.tiendaOrigen = "";
      this.numero = "";
      this.resultados = [];
      this.buscado = false;
      this.confirmando = null;
      this.limpiarError();
      if (!this.tiendas || this.tiendas.length === 0) {
        this.$store.dispatch("catalogos/llenar_tiendas", {
          activa: true,
          quitar_paginacion: true,
        });
      }
      if (this.desdeListado) {
        this.cargarTiendasDestino();
      }
      this.visible = true;
    },
    /**
     * Tiendas destino: solo las asignadas al usuario y activas (el backend lo vuelve a validar).
     * Se preselecciona la predeterminada o la unica asignada.
     */
    cargarTiendasDestino() {
      CotizacionMovilService.tiendasUsuario()
        .then((response) => {
          const asignadas = Array.isArray(response.data) ? response.data : response.data.results || [];
          this.tiendasDestino = asignadas
            .map((item) => item.tienda)
            .filter((tienda) => tienda && tienda.activa);
          const predeterminada = asignadas.find(
            (item) => item.predeterminada && item.tienda && item.tienda.activa
          );
          const unica = this.tiendasDestino.length === 1 ? this.tiendasDestino[0] : null;
          const inicial = predeterminada ? predeterminada.tienda : unica;
          if (inicial && !this.destinoSeleccionado) {
            this.destinoSeleccionado = inicial.codigo;
          }
        })
        .catch((error) => {
          this.tiendasDestino = [];
          this.mostrarError(error);
        });
    },
    cerrar() {
      if (this.trasladando) {
        return;
      }
      this.visible = false;
    },
    limpiarError() {
      this.error = "";
      this.erroresProductos = [];
    },
    buscar() {
      this.limpiarError();
      const numero = String(this.numero || "").trim();
      if (this.desdeListado && !this.destinoSeleccionado) {
        this.error = "Seleccione la tienda a donde se traslada.";
        return;
      }
      if (!this.tiendaOrigen) {
        this.error = "Seleccione la tienda donde se hizo la cotización.";
        return;
      }
      if (!/^\d+$/.test(numero)) {
        this.error = "Ingrese el número de la cotización.";
        return;
      }
      this.buscando = true;
      this.confirmando = null;
      const peticion = this.desdeListado
        ? CotizacionMovilService.buscarTrasladoATienda(this.destinoSeleccionado, this.tiendaOrigen, numero)
        : CotizacionMovilService.buscarParaTraslado(this.cotizacionId, this.tiendaOrigen, numero);
      peticion
        .then((response) => {
          this.resultados = Array.isArray(response.data) ? response.data : [];
          this.buscado = true;
        })
        .catch((error) => {
          this.resultados = [];
          this.mostrarError(error);
        })
        .finally(() => {
          this.buscando = false;
        });
    },
    trasladar(cot) {
      this.limpiarError();
      this.trasladando = true;
      const peticion = this.desdeListado
        ? CotizacionMovilService.trasladarATiendaDestino(this.destinoSeleccionado, cot.id)
        : CotizacionMovilService.trasladarATienda(this.cotizacionId, cot.id);
      peticion
        .then((response) => {
          this.trasladando = false;
          this.visible = false;
          this.$emit("trasladada", Object.assign({ numero_origen: cot.numero, tienda_origen: cot.tienda },
            response.data || {}));
        })
        .catch((error) => {
          this.trasladando = false;
          this.confirmando = null;
          this.mostrarError(error);
        });
    },
    mostrarError(error) {
      const data = error && error.response ? error.response.data : null;
      if (data && data.detail) {
        this.error = data.detail;
      } else if (data && typeof data === "object") {
        const primero = Object.values(data)[0];
        this.error = Array.isArray(primero) ? primero[0] : String(primero);
      } else {
        this.error = "No se pudo completar la operación.";
      }
      this.erroresProductos = data && Array.isArray(data.productos) ? data.productos : [];
    },
    formatoMonto(valor) {
      const numero = Number(valor || 0);
      return numero.toLocaleString("es-GT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatoFecha(fecha) {
      // yyyy-mm-dd -> dd/mm/yyyy
      if (!fecha) {
        return "-";
      }
      const partes = String(fecha).split("-");
      if (partes.length === 3) {
        return `${partes[2]}/${partes[1]}/${partes[0]}`;
      }
      return fecha;
    },
  },
};
</script>

<style scoped>
.tc-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  /* Debajo de los avisos (1100) y de sweetalert; encima de los modales de bootstrap (1050) */
  z-index: 1085;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  background-color: rgba(0, 0, 0, 0.6);
  overflow-y: auto;
}

.tc-panel {
  background-color: #fff;
  border-radius: 8px;
  width: 100%;
  max-width: 520px;
  max-height: 100%;
  overflow-y: auto;
}

.tc-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fa;
}

.tc-body {
  padding: 12px 16px 16px;
}

.tc-body .form-control {
  min-height: 44px;
}

.tc-item {
  border: 1px solid #e9ecef;
  border-left: 4px solid #24b314;
  border-radius: 6px;
  padding: 10px 12px;
  margin-top: 10px;
  background-color: #fff;
}

.tc-item-bloqueada {
  border-left-color: #e65252;
  background-color: #f8f8f8;
}

.tc-fila {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.tc-cliente {
  font-size: 0.95rem;
  margin: 2px 0;
  overflow-wrap: anywhere;
}

.tc-productos {
  padding-left: 18px;
  overflow-wrap: anywhere;
}

.tc-confirmar {
  margin-top: 8px;
  padding: 8px;
  border-radius: 6px;
  background-color: #f1f8f1;
}

.tc-btn,
.tc-confirmar .btn {
  min-height: 44px;
}
</style>
